"use client";

import { useT } from "@/i18n/provider";
import { ROLE_META, AGENT_ROLES, type AgentRole } from "@/lib/orchestration";
import { cn } from "@/lib/cn";

/**
 * Canonical "by whom or what" renderer for queue rows — the creator /
 * originator column counterpart to {@link RelativeTime}.
 *
 * A `created_by` (or equivalent) value is an *actor token*, not a name:
 *
 *   "usr_…"           → a human user id; shown as the user's display name
 *                       when the caller passes `name` (resolved server-side
 *                       via `attachActorNames`), verbatim otherwise
 *   "agent:frontend"  → an orchestration agent, shown as its role label
 *                       ("Frontend Agent")
 *   "system:slack"    → an automated source, shown as a friendly label
 *                       ("Slack"); bare "system" → "Systeem"/"System"
 *   "device:dsk_…"    → a desktop / API token, shown as "API"
 *   null / "" / "—"   → nothing recorded → muted em dash
 *
 * Pages that already store a plain agent name (Patches `agentName`) or a
 * raw source (Runs `agentId`, Knowledge `addedBy`) can pass it straight
 * through — an unprefixed value is rendered as-is.
 */
export type CreatedByProps = {
  /** Actor token, agent name, or source string. */
  value?: string | null;
  /**
   * Resolved display name for a human `value` (e.g. from a users join).
   * When set, it replaces the verbatim user id; the raw token stays in
   * the hover title so the id is still discoverable.
   */
  name?: string | null;
  size?: "xs" | "sm";
  className?: string;
};

const SIZE_CLS = {
  xs: "text-[10.5px]",
  sm: "text-[11.5px]",
} as const;

function isAgentRole(s: string): s is AgentRole {
  return (AGENT_ROLES as readonly string[]).includes(s);
}

/**
 * Pure token → label resolver, exported so non-React callers (CSV export,
 * tests) can reuse the exact same mapping. `t` is optional — without it the
 * generic source labels fall back to English.
 */
export function formatActorToken(
  value: string | null | undefined,
  t?: (key: string) => string,
): string {
  const raw = (value ?? "").trim();
  if (!raw || raw === "—") return "—";

  if (raw === "system") return t ? t("datatable.actor.system") : "System";

  if (raw.startsWith("system:")) {
    const src = raw.slice("system:".length);
    const key = `datatable.actor.source.${src}`;
    const label = t ? t(key) : "";
    // Translator returns the key unchanged when there's no entry — fall
    // back to a Title-cased source so an unknown channel still reads well.
    if (label && label !== key) return label;
    return src.charAt(0).toUpperCase() + src.slice(1);
  }

  if (raw.startsWith("agent:")) {
    const role = raw.slice("agent:".length);
    return isAgentRole(role) ? ROLE_META[role].label : `${role} agent`;
  }

  if (raw.startsWith("device:")) return t ? t("datatable.actor.device") : "API";

  // Bare agent role (e.g. Patches' agentName is sometimes a role key).
  if (isAgentRole(raw)) return ROLE_META[raw].label;

  // Otherwise it's a human user id or a plain name — show verbatim.
  return raw;
}

export function CreatedBy({ value, name, size = "xs", className }: CreatedByProps) {
  const t = useT();
  const resolved = (name ?? "").trim();
  const label = resolved || formatActorToken(value, t);
  const muted = label === "—";
  return (
    <span
      title={value ?? undefined}
      className={cn(
        "truncate whitespace-nowrap",
        muted ? "text-fg-muted/60" : "text-fg-muted",
        SIZE_CLS[size],
        className,
      )}
    >
      {label}
    </span>
  );
}
